'use strict'

import React, { useEffect, useState, useCallback, useRef } from 'react'
import { produce } from 'immer'
import { ChrofyShowErrors } from '../../../common/Form'
import { ChrofyCustomSelect } from '../../../common/ChrofyCustomSelect'
import { getMessage } from '../../../../common/utils/MessageUtils'

// --- Validation messages (設計書 §8.1)
const ERRORS = {
  FILE_TYPE_REQUIRED: 'ファイル種別を選択してください',
  COLUMN_NAME_REQUIRED: '項目名を選択してください'
}
const MSG_DUPLICATE_FROM = (value) => getMessage('E010013', `変換前の値「${value}」`)
const MSG_AT_LEAST_ONE_ROW = getMessage('E011017', '変換ルール')
// 変換前・変換後のどちらか一方のみ入力された不完全な行に対するメッセージ（該当行番号を明示）
const MSG_INCOMPLETE_ROW = (rowNo) => getMessage('E010001', `変換前・変換後（${rowNo}行目）`)

/**
 * MappingInfo for ValueMapping (設計書 §7.1): ValueMapping 配下に MappingList を持つ
 *
 * @typedef {{
 *   mappingType: 'ValueMapping'
 *   valueMapping: {
 *     mappingFileType: string
 *     mappingColumnName: string
 *     mappingList?: { from: string; to: string }[]
 *     MappingList?: { from: string; to: string }[]
 *   }
 * }} ValueMappingInfo
 */

/**
 * Build MappingInfo from form state (UI → DB §7.2)
 *
 * @param {{ mappingFileType: string; mappingColumnName: string; mappingList: { from: string; to: string }[] }} formData
 * @returns {{
 *   MappingType: 'ValueMapping'
 *   ValueMapping: {
 *     MappingFileType: string
 *     MappingColumnName: string
 *     MappingList: { from: string; to: string }[]
 *   }
 * }}
 */
export function formDataToMappingInfo(formData) {
  return {
    MappingType: 'ValueMapping',
    _valueMappingUiType: 'manual',
    ValueMapping: {
      MappingFileType: formData.mappingFileType ?? '',
      MappingColumnName: formData.mappingColumnName ?? '',
      MappingList: (formData.mappingList ?? [])
        .map((row) => ({
          from: String(row.from ?? '').trim(),
          to: String(row.to ?? '').trim()
        }))
        // 変換前・変換後の両方が入力された行のみを保存対象とする（不完全な行は除外）
        .filter((row) => row.from !== '' && row.to !== '')
    }
  }
}

/**
 * Build form state from initial MappingInfo (DB → UI §7.2) Accepts both camelCase (ValueMapping/MappingList) and
 * snake_case (value_mapping/mapping_list) from API.
 *
 * @param {ValueMappingInfo | null | undefined} initial
 * @param {import('../extra/types').FileTypeInfo[]} fileTypes
 * @returns {{ mappingFileType: string; mappingColumnName: string; mappingList: { from: string; to: string }[] }}
 */
export function mappingInfoToFormData(initial, fileTypes) {
  const vm = initial?.valueMapping ?? initial?.value_mapping
  if (!vm || typeof vm !== 'object') {
    return {
      mappingFileType: '',
      mappingColumnName: '',
      mappingList: [{ from: '', to: '' }]
    }
  }
  const rawList = vm.mappingList ?? vm.MappingList ?? vm.mapping_list
  const list =
    Array.isArray(rawList) && rawList.length > 0
      ? rawList.map((row) => ({ from: String(row.from ?? ''), to: String(row.to ?? '') }))
      : [{ from: '', to: '' }]
  return {
    mappingFileType: vm.mappingFileType ?? vm.MappingFileType ?? vm.mapping_file_type ?? '',
    mappingColumnName: vm.mappingColumnName ?? vm.MappingColumnName ?? vm.mapping_column_name ?? '',
    mappingList: list
  }
}

/**
 * Validate form: file type / column required, duplicate From forbidden, at least one row, no incomplete row (§8.1)
 *
 * @param {{ mappingFileType?: string; mappingColumnName?: string; mappingList?: { from: string; to: string }[] }} formData
 * @returns {Record<string, string[]> | null}
 */
export function validateValueMappingForm(formData) {
  const errors = /** @type {Record<string, string[]>} */ ({})

  const mappingFileType = (formData.mappingFileType ?? '').toString().trim()
  const mappingColumnName = (formData.mappingColumnName ?? '').toString().trim()

  if (!mappingFileType) {
    errors.mappingFileType = [ERRORS.FILE_TYPE_REQUIRED]
  }
  if (!mappingColumnName) {
    errors.mappingColumnName = [ERRORS.COLUMN_NAME_REQUIRED]
  }

  const list = formData.mappingList ?? []

  // 変換前・変換後のどちらか一方のみ入力された不完全な行を検出（XOR 判定）
  const incompleteRowNos = list
    .map((row, index) => ({
      index,
      from: String(row.from ?? '').trim(),
      to: String(row.to ?? '').trim()
    }))
    .filter((row) => (row.from !== '') !== (row.to !== ''))
    .map((row) => row.index + 1)
  if (incompleteRowNos.length > 0) {
    errors.mappingIncomplete = incompleteRowNos.map((no) => MSG_INCOMPLETE_ROW(no))
  }

  // 変換前・変換後の両方が入力された行のみを有効な行とみなす
  const nonEmptyRows = list.filter((row) => String(row.from ?? '').trim() !== '' && String(row.to ?? '').trim() !== '')
  // 不完全な行がある場合はそちらのエラーを優先し、「最低1つ」エラーは重複表示しない
  if (nonEmptyRows.length < 1 && incompleteRowNos.length === 0) {
    errors.mappingList = [MSG_AT_LEAST_ONE_ROW]
  }
  const fromValues = nonEmptyRows.map((r) => String(r.from ?? '').trim()).filter((v) => v !== '')
  const seen = new Set()
  for (const v of fromValues) {
    if (seen.has(v)) {
      errors.fromDuplicate = [MSG_DUPLICATE_FROM(v)]
      break
    }
    seen.add(v)
  }

  return Object.keys(errors).length > 0 ? errors : null
}

/**
 * ValueMappingInputForm — 値変換モーダル（手入力版）§4.1
 *
 * @param {{
 *   initialMappingInfo?: ValueMappingInfo | null
 *   fileTypes?: import('../extra/types').FileTypeInfo[]
 *   onChange?: (formData: unknown, setErrors: (e: Record<string, string[]>) => void) => void
 *   formData?: Record<string, unknown>
 *   errors?: Record<string, string[]>
 * }} props
 */
export const ValueMappingInputForm = (props) => {
  const {
    initialMappingInfo = null,
    fileTypes = [],
    onChange,
    formData: externalFormData,
    errors: externalErrors = {}
  } = props

  // Use external formData if provided (controlled mode), otherwise use internal (uncontrolled mode)
  const [internalFormData, setInternalFormData] = useState(() => mappingInfoToFormData(initialMappingInfo, fileTypes))
  const [errors, setErrors] = useState({ mappingList: [], fromDuplicate: [] })

  const isControlled = externalFormData !== undefined
  const formData = isControlled ? externalFormData : internalFormData
  const displayErrors = { ...errors, ...externalErrors }
  const formDataRef = useRef(formData)
  formDataRef.current = formData

  // In uncontrolled mode, notify parent when internal formData changes
  useEffect(() => {
    if (!isControlled) {
      onChange?.(internalFormData, setErrors)
    }
  }, [internalFormData, onChange, isControlled])

  // In controlled mode, notify parent on first mount
  useEffect(() => {
    if (isControlled) {
      onChange?.(formData, setErrors)
    }
  }, [])

  const updateForm = useCallback(
    (updater) => {
      const base =
        formDataRef.current && typeof formDataRef.current === 'object' && Array.isArray(formDataRef.current.mappingList)
          ? formDataRef.current
          : {
              ...(formDataRef.current && typeof formDataRef.current === 'object' ? formDataRef.current : {}),
              mappingList: [{ from: '', to: '' }]
            }
      const newData = typeof updater === 'function' ? produce(base, updater) : updater
      formDataRef.current = newData
      if (isControlled) {
        onChange?.(newData, setErrors)
      } else {
        setInternalFormData(newData)
      }
    },
    [isControlled, onChange]
  )

  const addRow = useCallback(() => {
    updateForm((draft) => {
      draft.mappingList = draft.mappingList ?? []
      draft.mappingList.push({ from: '', to: '' })
    })
  }, [updateForm])

  const removeRow = useCallback(
    (index) => {
      updateForm((draft) => {
        draft.mappingList = draft.mappingList ?? []
        draft.mappingList.splice(index, 1)
        if (draft.mappingList.length === 0) {
          draft.mappingList.push({ from: '', to: '' })
        }
      })
    },
    [updateForm]
  )

  const setRow = useCallback(
    (index, field, value) => {
      updateForm((draft) => {
        if (!draft.mappingList) draft.mappingList = [{ from: '', to: '' }]
        if (draft.mappingList[index]) {
          draft.mappingList[index][field] = value
        }
      })
    },
    [updateForm]
  )

  const selectedFileType = fileTypes?.find((f) => f.mappingFileType === formData.mappingFileType)
  const columnOptions = (selectedFileType?.headers ?? []).map((h) => ({ label: h, value: h }))
  const isColumnDisabled = !formData.mappingFileType

  const handleFileTypeChange = useCallback(
    (e) => {
      const mappingFileType = e.target.value ?? ''
      updateForm((draft) => {
        draft.mappingFileType = mappingFileType
        draft.mappingColumnName = ''
      })
    },
    [updateForm]
  )

  const handleColumnChange = useCallback(
    (e) => {
      const mappingColumnName = e.target.value ?? ''
      updateForm((draft) => {
        draft.mappingColumnName = mappingColumnName
      })
    },
    [updateForm]
  )

  return (
    <div className="text-h7">
      <div className="mb-4">
        <label className="block mb-1.5 font-medium text-[13px] text-gray-600">
          ファイル種別を選択 <span className="red--text">*</span>
        </label>
        <ChrofyCustomSelect
          id="value-mapping-input-file-type"
          name="value-mapping-input-file-type"
          value={formData.mappingFileType}
          onChange={handleFileTypeChange}
          className="w-full"
          options={(fileTypes ?? []).map((ft) => ({
            label: ft.mappingFileType,
            value: ft.mappingFileType
          }))}
          disabled={false}
          hasNoSelect={true}
          searchable
          isError={Boolean(displayErrors.mappingFileType?.length)}
          data-testid="value-mapping-input-file-type"
        />
        <ChrofyShowErrors field="mappingFileType" errors={displayErrors} />
      </div>
      <div className="mb-4">
        <label className="block mb-1.5 font-medium text-[13px] text-gray-600">
          項目選択 <span className="red--text">*</span>
        </label>
        <ChrofyCustomSelect
          id="value-mapping-input-column-name"
          name="value-mapping-input-column-name"
          value={formData.mappingColumnName}
          onChange={handleColumnChange}
          className={isColumnDisabled ? 'opacity-60 w-full' : 'w-full'}
          options={columnOptions}
          disabled={isColumnDisabled}
          hasNoSelect={true}
          searchable
          isError={Boolean(displayErrors.mappingColumnName?.length)}
          data-testid="value-mapping-input-column-name"
        />
        <ChrofyShowErrors field="mappingColumnName" errors={displayErrors} />
      </div>
      <div className="mt-5">
        <label className="block mb-2.5 font-medium text-[13px] text-gray-600">値のマッピング設定</label>
        <ChrofyShowErrors field="mappingList" errors={displayErrors} />
        <ChrofyShowErrors field="mappingIncomplete" errors={displayErrors} />
        <ChrofyShowErrors field="fromDuplicate" errors={displayErrors} />
        <div className="flex flex-col gap-2 mb-2">
          {(formData.mappingList ?? []).map((row, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type="text"
                className="text-input flex-1 text-left"
                placeholder="変換前の値"
                value={row.from}
                onChange={(e) => setRow(index, 'from', e.target.value)}
                data-testid={`value-mapping-input-from-${index}`}
              />
              <input
                type="text"
                className="text-input flex-1 text-left"
                placeholder="変換後の値"
                value={row.to}
                onChange={(e) => setRow(index, 'to', e.target.value)}
                data-testid={`value-mapping-input-to-${index}`}
              />
              <button
                type="button"
                className="btn red white--text text-sm shrink-0 px-3 py-2 rounded-lg"
                onClick={() => removeRow(index)}
                data-testid={`value-mapping-input-delete-${index}`}
              >
                削除
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="bg-[#E0E5F2] text-[#3150A5] hover:bg-[#d0d5e2] text-sm py-2 px-3 rounded"
          onClick={addRow}
          data-testid="value-mapping-input-add-row"
        >
          行を追加
        </button>
      </div>
    </div>
  )
}

export default ValueMappingInputForm
